import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Appointments from '../components/Appointments';
import './Dashboard.css';

const API_URL = window.location.origin;

const CHECKLIST_ITEMS = [
  { key: 'business_profile', label: 'Business profile created' },
  { key: 'services_hours',   label: 'Services + hours configured' },
  { key: 'ai_faqs',          label: 'AI trained on your FAQs' },
  { key: 'tone_voice',       label: 'Tone and voice set' },
  { key: 'widget_ready',     label: 'Widget tested and ready' },
  { key: 'live',             label: "You're live on Chatflo ✓", isFinal: true }
];

function SetupInProgress({ checklist, businessName }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f4f3 0%, #e8f0ed 100%)',
      padding: '40px 20px', fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 48 }}>
        <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
          <rect width="28" height="28" rx="7" fill="#2E5E4E"/>
          <path d="M8 20V8h4.2l2.8 5.6L17.8 8H22v12h-3.5V13.3L16 18.2h-2.1L11.5 13.3V20H8z" fill="white"/>
        </svg>
        <span style={{ fontWeight: 700, fontSize: 20, color: '#1F4538' }}>Chatflo</span>
      </div>

      <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1F4538', margin: '0 0 12px', textAlign: 'center' }}>
        We're setting up your Chatflo.
      </h1>
      <p style={{ fontSize: 16, color: '#5a7a6e', margin: '0 0 48px', textAlign: 'center', maxWidth: 480, lineHeight: 1.6 }}>
        Your AI is being trained and configured for {businessName ? <strong>{businessName}</strong> : 'your business'}.
        We'll notify you the moment you're live.
      </p>

      <div style={{
        background: 'white', borderRadius: 16, padding: '32px 40px',
        width: '100%', maxWidth: 480, boxShadow: '0 4px 24px rgba(0,0,0,0.07)'
      }}>
        <p style={{
          fontWeight: 700, color: '#2E5E4E', fontSize: 12,
          textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 20px'
        }}>
          Setup Progress
        </p>
        {CHECKLIST_ITEMS.map((item) => {
          const done = !!(checklist && checklist[item.key]);
          return (
            <div key={item.key} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 0',
              borderBottom: item.isFinal ? 'none' : '1px solid #f0f4f2',
              borderTop: item.isFinal ? '1px solid #d4e6dd' : 'none',
              marginTop: item.isFinal ? 8 : 0
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: done ? '#2E5E4E' : '#e8f0ed',
                border: done ? 'none' : '2px solid #b5cfc6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.3s ease'
              }}>
                {done && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6l3 3 4-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span style={{
                fontSize: 15, color: done ? '#1F4538' : '#7a9b8a',
                fontWeight: done ? 600 : 400
              }}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>

      <p style={{ marginTop: 32, fontSize: 13, color: '#8aab9e', textAlign: 'center' }}>
        Questions? Reply to your welcome email and we'll get back to you same day.
      </p>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [currentView, setCurrentView] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [stats, setStats] = useState({ conversations: 0, leads: 0, avgScore: 0 });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupChecklist, setSetupChecklist] = useState({});
  const [isBeta, setIsBeta] = useState(true);
  const [showLiveBanner, setShowLiveBanner] = useState(false);
  const [leadsData, setLeadsData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const businessName = localStorage.getItem('businessName') || '';

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) {
      navigate('/login');
      return;
    }
    setToken(savedToken);
    fetchData(savedToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!token || setupStatus === 'live' || setupStatus === null) return;
    const interval = setInterval(() => {
      fetchSetupStatus(token);
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, setupStatus]);

  const fetchData = async (authToken) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/chat/conversations`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const convos = response.data.conversations || [];
      setConversations(convos);
      const leads = convos.filter(c => c.lead_name);
      const totalScore = leads.reduce((sum, c) => sum + (c.score || 0), 0);
      setStats({
        conversations: convos.length,
        leads: leads.length,
        avgScore: leads.length > 0 ? Math.round(totalScore / leads.length) : 0
      });
      await fetchSetupStatus(authToken);
      await fetchBillingData(authToken);
      fetchKnowledgeDocs(authToken);
      fetchUsageData(authToken);
      fetchLeadsData(authToken);
      fetchAnalyticsData(authToken);
      fetchSettingsData(authToken);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async (authToken) => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/billing/plans`),
        axios.get(`${API_URL}/billing/subscription`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    }
  };

  const handleSubscribe = async (tier) => {
    setBillingLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/billing/create-checkout`,
        { tier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start checkout');
    } finally {
      setBillingLoading(false);
    }
  };

  const fetchKnowledgeDocs = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/knowledge`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setKnowledgeDocs(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch knowledge docs:', error);
    }
  };

  const fetchUsageData = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/billing/usage`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    }
  };

  const fetchLeadsData = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/leads`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setLeadsData(response.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const fetchAnalyticsData = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/analytics/summary`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchSettingsData = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/settings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const { business, services } = response.data;
      const aiConfig = business.ai_config || {};
      setSettingsForm({
        name: business.name || '',
        phone: business.phone || '',
        website: business.website || '',
        serviceArea: business.service_area || '',
        hours: business.hours || '',
        aiTone: business.ai_personality || 'professional',
        greeting: aiConfig.greeting || '',
        calendlyUrl: business.calendly_url || '',
        services: services.map(s => ({
          id: s.id,
          name: s.name,
          description: s.description || '',
          priceRange: s.price_range || '',
          duration: s.duration || 60
        }))
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchSetupStatus = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/auth/setup-status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const { setupStatus: status, setupChecklist: checklist, isBeta: beta, subscriptionStatus: subStatus } = response.data;

      // Gate: redirect to pricing if no active subscription
      const paidStatuses = ['active', 'free', 'canceling', 'past_due'];
      if (subStatus && !paidStatuses.includes(subStatus)) {
        window.location.href = '/pricing';
        return null;
      }

      const wasNotLive = setupStatus !== 'live';
      setSetupStatus(status);
      setSetupChecklist(checklist || {});
      setIsBeta(beta !== false);
      if (wasNotLive && status === 'live') {
        setShowLiveBanner(true);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch setup status:', error);
      setSetupStatus('live');
      return null;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/knowledge/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      await fetchKnowledgeDocs(token);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
      event.target.value = '';
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document from your knowledge base?')) return;
    try {
      await axios.delete(`${API_URL}/knowledge/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchKnowledgeDocs(token);
    } catch (error) {
      alert('Failed to delete document');
    }
  };

  const handleUpgrade = async (tier = 'pro') => {
    setBillingLoading(true);
    try {
      await axios.post(
        `${API_URL}/billing/upgrade`,
        { tier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Upgraded to ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan!`);
      await fetchBillingData(token);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upgrade');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will retain access until the end of your billing period.')) return;
    setBillingLoading(true);
    try {
      await axios.post(`${API_URL}/billing/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Subscription will be canceled at the end of the billing period.');
      await fetchBillingData(token);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to cancel');
    } finally {
      setBillingLoading(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      const response = await axios.post(`${API_URL}/billing/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      alert('Failed to open billing portal');
    }
  };

  const handleLeadStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/leads/${leadId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadsData(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update lead status');
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsForm) return;
    setSettingsSaving(true);
    setSettingsSaved(false);
    try {
      await axios.put(`${API_URL}/settings`, settingsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettingsSaved(true);
      if (settingsForm.name) localStorage.setItem('businessName', settingsForm.name);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateService = (idx, field, value) => {
    setSettingsForm(f => {
      const services = [...f.services];
      services[idx] = { ...services[idx], [field]: value };
      return { ...f, services };
    });
  };

  const viewConversation = async (convId) => {
    try {
      const response = await axios.get(`${API_URL}/chat/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedConversation(response.data);
      setCurrentView('conversation');
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('businessId');
    localStorage.removeItem('apiKey');
    localStorage.removeItem('businessName');
    navigate('/');
  };

  const handleNavClick = (view) => {
    setCurrentView(view);
    setSidebarOpen(false);
  };

  const conversionRate = stats.conversations > 0 ? Math.round((stats.leads / stats.conversations) * 100) : 0;

  const navItems = [
    { id: 'overview', label: 'Overview', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="11" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="2" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="11" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/></svg>
    )},
    { id: 'conversations', label: 'Conversations', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 4h14a1 1 0 011 1v8a1 1 0 01-1 1h-3l-4 3v-3H3a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
    )},
    { id: 'leads', label: 'Leads', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 17c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="14" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M13 12c1-.6 2.2-1 3.5-1 2.5 0 4.5 2 4.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    )},
    { id: 'appointments', label: 'Appointments', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 8h16M6 2v4M14 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    )},
    { id: 'embed', label: 'Embed Widget', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 5L2 10l5 5M13 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
    )},
    { id: 'knowledge', label: 'Knowledge Base', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 4h5l2 2h7a1 1 0 011 1v9a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    )},
    { id: 'usage', label: 'Usage', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="10" width="3" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="8.5" y="6" width="3" height="11" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="3" width="3" height="14" rx="1" stroke="currentColor" strokeWidth="1.5"/></svg>
    )},
    { id: 'billing', label: 'Billing', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 8h16" stroke="currentColor" strokeWidth="1.5"/><path d="M5 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    )},
    { id: 'settings', label: 'Settings', icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    )}
  ];

  const viewTitles = {
    overview: 'Dashboard Overview',
    conversations: 'All Conversations',
    conversation: 'Conversation Details',
    leads: 'Lead Management',
    appointments: 'Appointments',
    embed: 'Embed Your Widget',
    knowledge: 'Knowledge Base',
    usage: 'Usage & Analytics',
    billing: 'Billing & Subscription',
    settings: 'Widget Settings'
  };

  if (!token) return null;
  if (setupStatus === null) return null;
  if (setupStatus !== 'live') {
    return <SetupInProgress checklist={setupChecklist} businessName={businessName} />;
  }

  return (
    <div className="dash-layout">
      {sidebarOpen && <div className="dash-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dash-sidebar ${sidebarOpen ? 'dash-sidebar--open' : ''}`}>
        <div className="dash-sidebar__header">
          <div className="dash-sidebar__logo">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="7" fill="var(--color-primary)"/>
              <path d="M8 20V8h4.2l2.8 5.6L17.8 8H22v12h-3.5V13.3L16 18.2h-2.1L11.5 13.3V20H8z" fill="white"/>
            </svg>
            <span className="dash-sidebar__brand">Chatflo</span>
          </div>
          {businessName && <p className="dash-sidebar__business">{businessName}</p>}
        </div>

        <nav className="dash-sidebar__nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`dash-sidebar__link ${currentView === item.id ? 'dash-sidebar__link--active' : ''}`}
              onClick={() => handleNavClick(item.id)}
            >
              <span className="dash-sidebar__icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <button className="dash-sidebar__logout" onClick={handleLogout}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 17H4a1 1 0 01-1-1V4a1 1 0 011-1h3M13 14l4-4-4-4M17 10H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Sign Out
        </button>
      </aside>

      <main className="dash-main">
        <header className="dash-topbar">
          <div className="dash-topbar__left">
            <button className="dash-topbar__hamburger" onClick={() => setSidebarOpen(true)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <h1 className="dash-topbar__title">{viewTitles[currentView] || ''}</h1>
          </div>
          <div className="dash-topbar__right">
            <button className="dash-topbar__refresh" onClick={() => fetchData(token)} disabled={loading}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className={loading ? 'spin' : ''}>
                <path d="M15.5 9A6.5 6.5 0 113.3 5.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M15.5 3v3.5H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
          </div>
        </header>

        <div className="dash-content">
          {currentView === 'overview' && (
            <div className="dash-overview">
              <div className="dash-overview__greeting">
                <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 16px' }}>
                  Welcome back{businessName ? `, ${businessName}` : ''}.
                </h2>
              </div>
              {showLiveBanner && (
                <div style={{
                  background: 'var(--color-primary)', color: 'white', padding: '12px 16px',
                  borderRadius: 10, marginBottom: 16, display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', fontSize: 14, fontWeight: 500
                }}>
                  <span>Your Chatflo AI is live and ready to capture leads.</span>
                  <button onClick={() => setShowLiveBanner(false)} style={{
                    background: 'none', border: 'none', color: 'white', cursor: 'pointer',
                    fontSize: 20, lineHeight: 1, padding: '0 4px'
                  }}>&#215;</button>
                </div>
              )}
              {isBeta && (
                <div style={{
                  background: '#f0f7f4', border: '1px solid #b5cfc6', borderRadius: 10,
                  padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#1F4538',
                  display: 'flex', alignItems: 'center', gap: 10
                }}>
                  <span>🚀</span>
                  <span>You're one of our first — your feedback directly shapes Chatflo. Reach out anytime.</span>
                </div>
              )}
              <div className="dash-stats">
                <div className="dash-stat-card">
                  <div className="dash-stat-card__icon dash-stat-card__icon--primary">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 5h16a1 1 0 011 1v10a1 1 0 01-1 1h-4l-5 4v-4H4a1 1 0 01-1-1V6a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5"/></svg>
                  </div>
                  <div className="dash-stat-card__body">
                    <span className="dash-stat-card__number">{stats.conversations}</span>
                    <span className="dash-stat-card__label">Total Conversations</span>
                  </div>
                  {analyticsData
                    ? <span className={`dash-stat-card__trend dash-stat-card__trend--${analyticsData.convTrendPct > 0 ? 'up' : analyticsData.convTrendPct < 0 ? 'down' : 'neutral'}`}>
                        {analyticsData.convTrendPct > 0 ? '+' : ''}{analyticsData.convTrendPct}% vs last week
                      </span>
                    : <span className="dash-stat-card__trend dash-stat-card__trend--neutral">--</span>
                  }
                </div>

                <div className="dash-stat-card">
                  <div className="dash-stat-card__icon dash-stat-card__icon--success">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M17 11l2 2 3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="dash-stat-card__body">
                    <span className="dash-stat-card__number">{stats.leads}</span>
                    <span className="dash-stat-card__label">Captured Leads</span>
                  </div>
                  {analyticsData
                    ? <span className="dash-stat-card__trend dash-stat-card__trend--up">{analyticsData.leadsThisWeek} this week</span>
                    : <span className="dash-stat-card__trend dash-stat-card__trend--neutral">--</span>
                  }
                </div>

                <div className="dash-stat-card">
                  <div className="dash-stat-card__icon dash-stat-card__icon--warning">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="dash-stat-card__body">
                    <span className="dash-stat-card__number">{stats.avgScore}</span>
                    <span className="dash-stat-card__label">Avg Lead Score</span>
                  </div>
                  <span className="dash-stat-card__trend dash-stat-card__trend--neutral">--</span>
                </div>

                <div className="dash-stat-card">
                  <div className="dash-stat-card__icon dash-stat-card__icon--secondary">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><polyline points="22,7 13.5,15.5 8.5,10.5 2,17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><polyline points="16,7 22,7 22,13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="dash-stat-card__body">
                    <span className="dash-stat-card__number">{conversionRate}%</span>
                    <span className="dash-stat-card__label">Conversion Rate</span>
                  </div>
                  {analyticsData
                    ? <span className="dash-stat-card__trend dash-stat-card__trend--up">{analyticsData.bookingRate}% booking rate</span>
                    : <span className="dash-stat-card__trend dash-stat-card__trend--neutral">--</span>
                  }
                </div>
              </div>

              <div className="dash-activity">
                <h3 className="dash-activity__title">Recent Activity</h3>
                {conversations.length === 0 ? (
                  <p className="dash-activity__empty">Your Chatflo AI is live and listening. Leads will appear here the moment a conversation starts.</p>
                ) : (
                  <div className="dash-activity__list">
                    {conversations.slice(0, 5).map(convo => (
                      <div key={convo.id} className="dash-activity__item" onClick={() => viewConversation(convo.id)}>
                        <div className="dash-activity__avatar">
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M3 16c0-2.8 2.7-5 6-5s6 2.2 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        </div>
                        <div className="dash-activity__info">
                          <span className="dash-activity__name">{convo.lead_name || 'Anonymous'}</span>
                          <span className="dash-activity__meta">{convo.channel} &middot; {convo.message_count} messages</span>
                        </div>
                        <span className="dash-activity__date">{new Date(convo.started_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {currentView === 'conversations' && (
            <div className="dash-conversations">
              {conversations.length === 0 ? (
                <div className="dash-empty">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <rect x="8" y="12" width="48" height="36" rx="6" stroke="var(--color-neutral-300)" strokeWidth="2"/>
                    <path d="M8 24h48" stroke="var(--color-neutral-300)" strokeWidth="2"/>
                    <circle cx="32" cy="38" r="6" stroke="var(--color-neutral-300)" strokeWidth="2"/>
                    <path d="M29 38l2 2 4-4" stroke="var(--color-neutral-300)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>No conversations yet</h3>
                  <p>Your Chatflo AI is live and listening. Leads will appear here the moment a conversation starts.</p>
                </div>
              ) : (
                <div className="dash-convo-grid">
                  {conversations.map(convo => (
                    <div key={convo.id} className="dash-convo-card" onClick={() => viewConversation(convo.id)}>
                      <div className="dash-convo-card__top">
                        <span className="dash-convo-card__channel">{convo.channel}</span>
                        <span className="dash-convo-card__date">{new Date(convo.started_at).toLocaleDateString()}</span>
                      </div>
                      <div className="dash-convo-card__body">
                        <h4>{convo.lead_name || 'Anonymous'}</h4>
                        {convo.phone && <p className="dash-convo-card__contact"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 3.5C2 2.67 2.67 2 3.5 2h1.83a.5.5 0 01.47.33l.83 2.17a.5.5 0 01-.12.53l-1.18 1.18a7.5 7.5 0 003.45 3.45l1.18-1.18a.5.5 0 01.53-.12l2.17.83a.5.5 0 01.33.47V11.5c0 .83-.67 1.5-1.5 1.5A9.5 9.5 0 012 3.5z" stroke="currentColor" strokeWidth="1.2"/></svg> {convo.phone}</p>}
                        {convo.email && <p className="dash-convo-card__contact"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M1 4.5l6 4 6-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg> {convo.email}</p>}
                      </div>
                      <div className="dash-convo-card__footer">
                        <span className="dash-convo-card__msgs">{convo.message_count} messages</span>
                        {convo.score > 0 && (
                          <span className={`dash-score dash-score--${convo.score >= 70 ? 'high' : convo.score >= 40 ? 'mid' : 'low'}`}>
                            {convo.score}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {currentView === 'conversation' && selectedConversation && (
            <div className="dash-detail">
              <button className="dash-detail__back" onClick={() => setCurrentView('conversations')}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Back to Conversations
              </button>

              <div className="dash-detail__header">
                <div className="dash-detail__lead">
                  <h2>{selectedConversation.lead?.name || 'Anonymous'}</h2>
                  <div className="dash-detail__contact-row">
                    {selectedConversation.lead?.phone && <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 3.5C2 2.67 2.67 2 3.5 2h1.83a.5.5 0 01.47.33l.83 2.17a.5.5 0 01-.12.53l-1.18 1.18a7.5 7.5 0 003.45 3.45l1.18-1.18a.5.5 0 01.53-.12l2.17.83a.5.5 0 01.33.47V11.5c0 .83-.67 1.5-1.5 1.5A9.5 9.5 0 012 3.5z" stroke="currentColor" strokeWidth="1.2"/></svg> {selectedConversation.lead.phone}</span>}
                    {selectedConversation.lead?.email && <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M1 4.5l6 4 6-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg> {selectedConversation.lead.email}</span>}
                    {selectedConversation.lead?.score > 0 && (
                      <span className={`dash-score dash-score--${selectedConversation.lead.score >= 70 ? 'high' : selectedConversation.lead.score >= 40 ? 'mid' : 'low'}`}>
                        Score: {selectedConversation.lead.score}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="dash-messages">
                {selectedConversation.messages?.map((msg, idx) => (
                  <div key={idx} className={`dash-msg ${msg.sender_type === 'user' ? 'dash-msg--user' : 'dash-msg--bot'}`}>
                    <div className="dash-msg__bubble">
                      <p>{msg.content}</p>
                      <span className="dash-msg__time">{new Date(msg.created_at).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentView === 'leads' && (
            <div className="dash-leads">
              {leadsData.length === 0 ? (
                <div className="dash-empty">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="24" cy="22" r="8" stroke="var(--color-neutral-300)" strokeWidth="2"/>
                    <path d="M10 48c0-7.7 6.3-14 14-14s14 6.3 14 14" stroke="var(--color-neutral-300)" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="44" cy="20" r="6" stroke="var(--color-neutral-300)" strokeWidth="2"/>
                    <path d="M38 48c0-5.5 3.6-10 8-10" stroke="var(--color-neutral-300)" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <h3>No leads captured yet</h3>
                  <p>Leads will appear here once visitors share their information through the widget.</p>
                </div>
              ) : (
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Date</th>
                        <th>Conversation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsData.map(lead => (
                        <tr key={lead.id}>
                          <td><strong>{lead.name || 'Unknown'}</strong></td>
                          <td>{lead.phone || '--'}</td>
                          <td>{lead.email || '--'}</td>
                          <td><span className="dash-convo-card__channel">{lead.source || lead.channel || '--'}</span></td>
                          <td onClick={e => e.stopPropagation()}>
                            <select
                              className={`dash-lead-status dash-lead-status--${lead.status}`}
                              value={lead.status}
                              onChange={e => handleLeadStatusChange(lead.id, e.target.value)}
                            >
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="replied">Replied</option>
                              <option value="booked">Booked</option>
                              <option value="closed">Closed</option>
                              <option value="lost">Lost</option>
                            </select>
                          </td>
                          <td>
                            {lead.score > 0 && (
                              <span className={`dash-score dash-score--${lead.score >= 70 ? 'high' : lead.score >= 40 ? 'mid' : 'low'}`}>
                                {lead.score}
                              </span>
                            )}
                          </td>
                          <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                          <td>
                            {lead.conversation_id
                              ? <button className="dash-lead-link" onClick={() => viewConversation(lead.conversation_id)}>View</button>
                              : '--'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {currentView === 'appointments' && (
            <Appointments token={token} />
          )}

          {currentView === 'embed' && (
            <div className="dash-embed">
              <div className="dash-embed__intro">
                <h3>Add the AI Booking Widget to Your Website</h3>
                <p>Copy the embed code below and paste it into your site's HTML to start capturing leads and booking appointments automatically.</p>
              </div>

              <div className="dash-embed__code-box">
                <div className="dash-embed__code-header">
                  <span>HTML Embed Code</span>
                  <button
                    className="dash-embed__copy"
                    onClick={() => {
                      const businessId = localStorage.getItem('businessId');
                      const scriptEnd = String.fromCharCode(60) + '/script>';
                      const code = `<!-- AI Booking Widget -->\n<div id="ai-booking-widget" data-business-id="${businessId}"></div>\n<script src="${window.location.origin}/widget.js">${scriptEnd}`;
                      navigator.clipboard.writeText(code);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? (
                      <><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> Copied!</>
                    ) : (
                      <><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="4" y="4" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M10 4V2.5A1.5 1.5 0 008.5 1h-6A1.5 1.5 0 001 2.5v6A1.5 1.5 0 002.5 10H4" stroke="currentColor" strokeWidth="1.2"/></svg> Copy Code</>
                    )}
                  </button>
                </div>
                <pre className="dash-embed__code">
{`<!-- AI Booking Widget -->
<div id="ai-booking-widget"
     data-business-id="${localStorage.getItem('businessId')}">
</div>
<script src="${window.location.origin}/widget.js"></script>`}
                </pre>
              </div>

              <div className="dash-embed__instructions">
                <h4>Installation Steps</h4>
                <ol>
                  <li>Copy the embed code above</li>
                  <li>Open your website's HTML file or CMS editor</li>
                  <li>Paste the code before the closing <code>&lt;/body&gt;</code> tag</li>
                  <li>Save and publish your site</li>
                </ol>

                <h4>Widget Features</h4>
                <ul>
                  <li>AI-powered conversational assistant</li>
                  <li>Appointment booking with calendar integration</li>
                  <li>Automatic lead capture and scoring</li>
                  <li>Real-time follow-up notifications</li>
                  <li>Fully responsive on all devices</li>
                </ul>
              </div>
            </div>
          )}

          {currentView === 'knowledge' && (
            <div className="dash-knowledge">
              <div className="dash-knowledge__header">
                <div>
                  <h3>Knowledge Base Documents</h3>
                  <p>Upload documents to help your AI give more accurate, personalized responses. Supported formats: PDF, DOCX, TXT</p>
                </div>
                <label className="dash-btn dash-btn--primary" style={{ cursor: 'pointer' }}>
                  <input type="file" accept=".pdf,.docx,.txt,.doc" onChange={handleFileUpload} style={{ display: 'none' }} />
                  {uploadLoading ? 'Uploading...' : 'Upload Document'}
                </label>
              </div>

              {knowledgeDocs.length === 0 ? (
                <div className="dash-empty">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <rect x="12" y="8" width="40" height="48" rx="4" stroke="var(--color-neutral-300)" strokeWidth="2"/>
                    <path d="M22 24h20M22 32h20M22 40h12" stroke="var(--color-neutral-300)" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <h3>No documents uploaded yet</h3>
                  <p>Upload PDF, DOCX, or TXT files to enhance your AI's knowledge about your business.</p>
                </div>
              ) : (
                <div className="dash-knowledge__list">
                  {knowledgeDocs.map(doc => (
                    <div key={doc.id} className="dash-knowledge__item">
                      <div className="dash-knowledge__icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M8 8h8M8 12h8M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </div>
                      <div className="dash-knowledge__info">
                        <span className="dash-knowledge__name">{doc.original_name}</span>
                        <span className="dash-knowledge__meta">
                          {doc.file_type.toUpperCase()} &middot; {(doc.file_size / 1024).toFixed(1)} KB &middot; {new Date(doc.uploaded_at).toLocaleDateString()}
                        </span>
                      </div>
                      <button className="dash-knowledge__delete" onClick={() => handleDeleteDoc(doc.id)} title="Delete document">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {currentView === 'usage' && (
            <div className="dash-usage">
              {usageData ? (
                <>
                  <div className="dash-usage__summary">
                    <div className="dash-stat-card">
                      <div className="dash-stat-card__icon dash-stat-card__icon--primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 5h16a1 1 0 011 1v10a1 1 0 01-1 1h-4l-5 4v-4H4a1 1 0 01-1-1V6a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5"/></svg>
                      </div>
                      <div className="dash-stat-card__body">
                        <span className="dash-stat-card__number">{usageData.conversationsUsed}</span>
                        <span className="dash-stat-card__label">Conversations Used</span>
                      </div>
                    </div>
                    <div className="dash-stat-card">
                      <div className="dash-stat-card__icon dash-stat-card__icon--success">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M7 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </div>
                      <div className="dash-stat-card__body">
                        <span className="dash-stat-card__number">{usageData.conversationLimit}</span>
                        <span className="dash-stat-card__label">Monthly Limit</span>
                      </div>
                    </div>
                    <div className="dash-stat-card">
                      <div className="dash-stat-card__icon dash-stat-card__icon--warning">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><polyline points="22,7 13.5,15.5 8.5,10.5 2,17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      <div className="dash-stat-card__body">
                        <span className="dash-stat-card__number">{usageData.percentUsed}%</span>
                        <span className="dash-stat-card__label">Capacity Used</span>
                      </div>
                    </div>
                    <div className="dash-stat-card">
                      <div className="dash-stat-card__icon dash-stat-card__icon--secondary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/><path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </div>
                      <div className="dash-stat-card__body">
                        <span className="dash-stat-card__number">${usageData.overageCost.toFixed(2)}</span>
                        <span className="dash-stat-card__label">Overage Charges</span>
                      </div>
                    </div>
                  </div>

                  <div className="dash-usage__bar-section">
                    <h3>Usage This Period</h3>
                    <div className="dash-billing__bar" style={{ height: '24px', borderRadius: '12px' }}>
                      <div
                        className="dash-billing__bar-fill"
                        style={{
                          width: `${Math.min(usageData.percentUsed, 100)}%`,
                          backgroundColor: usageData.percentUsed > 90 ? 'var(--color-danger)' : usageData.percentUsed > 70 ? 'var(--color-warning)' : 'var(--color-success)',
                          borderRadius: '12px',
                          height: '100%',
                          transition: 'width 0.5s ease'
                        }}
                      />
                    </div>
                    <p style={{ marginTop: '8px', color: 'var(--color-neutral-500)', fontSize: '14px' }}>
                      {usageData.conversationsUsed} of {usageData.conversationLimit} conversations &middot; {usageData.tierName} plan
                      {usageData.overageCount > 0 && ` \u00b7 ${usageData.overageCount} overage ($${usageData.overageCost.toFixed(2)})`}
                    </p>
                  </div>

                  {usageData.channelBreakdown && usageData.channelBreakdown.length > 0 && (
                    <div className="dash-usage__channels">
                      <h3>Conversations by Channel</h3>
                      <div className="dash-usage__channel-grid">
                        {usageData.channelBreakdown.map((ch, idx) => (
                          <div key={idx} className="dash-usage__channel-card">
                            <span className="dash-usage__channel-name">{ch.channel || 'Unknown'}</span>
                            <span className="dash-usage__channel-count">{ch.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {usageData.dailyUsage && usageData.dailyUsage.length > 0 && (
                    <div className="dash-usage__daily">
                      <h3>Daily Activity (Last 30 Days)</h3>
                      <div className="dash-usage__daily-chart">
                        {usageData.dailyUsage.map((day, idx) => {
                          const maxCount = Math.max(...usageData.dailyUsage.map(d => parseInt(d.count)));
                          const height = maxCount > 0 ? (parseInt(day.count) / maxCount) * 100 : 0;
                          return (
                            <div key={idx} className="dash-usage__daily-bar-wrap" title={`${day.date}: ${day.count} conversations`}>
                              <div className="dash-usage__daily-bar" style={{ height: `${Math.max(height, 4)}%` }} />
                              <span className="dash-usage__daily-label">{new Date(day.date).getDate()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="dash-empty">
                  <p>Loading usage data...</p>
                </div>
              )}
            </div>
          )}

          {currentView === 'billing' && (
            <div className="dash-billing">
              {subscription && subscription.status && (
                <div className="dash-billing__current">
                  <h3>Current Subscription</h3>
                  <div className="dash-billing__card">
                    <div className="dash-billing__info">
                      <div className="dash-billing__tier-row">
                        <span className="dash-billing__tier-name">{subscription.tierName || 'No Plan'}</span>
                        <span className={`dash-billing__status dash-billing__status--${subscription.status}`}>
                          {subscription.status}
                        </span>
                      </div>
                      {subscription.status === 'active' && (
                        <div className="dash-billing__usage">
                          <p><strong>Conversations this period:</strong> {subscription.conversationsUsed} / {subscription.conversationLimit}</p>
                          <div className="dash-billing__bar">
                            <div
                              className="dash-billing__bar-fill"
                              style={{
                                width: `${Math.min((subscription.conversationsUsed / subscription.conversationLimit) * 100, 100)}%`,
                                backgroundColor: subscription.conversationsUsed > subscription.conversationLimit ? 'var(--color-danger)' : 'var(--color-success)'
                              }}
                            />
                          </div>
                          {subscription.conversationsUsed > subscription.conversationLimit && (
                            <p className="dash-billing__overage">
                              {subscription.conversationsUsed - subscription.conversationLimit} overage conversations ($2.50 each)
                            </p>
                          )}
                        </div>
                      )}
                      {subscription.periodEnd && (
                        <p className="dash-billing__period">
                          {subscription.status === 'canceling' ? 'Access until: ' : 'Renews: '}
                          {new Date(subscription.periodEnd).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="dash-billing__actions">
                      {subscription.status === 'active' && subscription.tier !== 'pro' && subscription.tier !== 'custom' && (
                        <button className="dash-btn dash-btn--primary" onClick={() => {
                          const nextTier = subscription.tier === 'starter' ? 'growth' : subscription.tier === 'growth' ? 'pro' : null;
                          if (nextTier) handleUpgrade(nextTier);
                        }} disabled={billingLoading}>
                          Upgrade Plan
                        </button>
                      )}
                      {subscription.status === 'active' && (
                        <>
                          <button className="dash-btn dash-btn--secondary" onClick={openBillingPortal}>
                            Manage Payment
                          </button>
                          <button className="dash-btn dash-btn--danger" onClick={handleCancel} disabled={billingLoading}>
                            Cancel Subscription
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(!subscription || !subscription.status || subscription.status === 'canceled') && (
                <div className="dash-billing__plans">
                  <h3>Choose a Plan</h3>
                  <div className="dash-billing__plans-grid">
                    {plans.length > 0 ? plans.map(plan => (
                      <div key={plan.id} className={`dash-plan ${plan.popular === true || plan.id === 'growth' ? 'dash-plan--featured' : ''}`}>
                        {(plan.popular === true || plan.id === 'growth') && <div className="dash-plan__badge">Most Popular</div>}
                        <h4>{plan.name}</h4>
                        <div className="dash-plan__price">
                          {plan.price != null
                            ? <><span className="dash-plan__amount">${plan.price}</span><span className="dash-plan__period">/month</span></>
                            : <span className="dash-plan__amount" style={{fontSize:'1.1rem'}}>Let's talk</span>
                          }
                        </div>
                        <p className="dash-plan__limit">
                          {plan.conversationLimit != null ? `${plan.conversationLimit} conversations/month` : 'Tailored volume'}
                        </p>
                        <p className="dash-plan__overage">
                          {plan.id === 'custom' ? 'Custom overage terms' : `+$${plan.overagePrice?.toFixed(2)} per extra conversation`}
                        </p>
                        <ul className="dash-plan__features">
                          {plan.features?.map((feature, idx) => (
                            <li key={idx}>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="var(--color-success)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                        {plan.id === 'free' ? (
                          <div className="dash-plan__current">Your starting plan</div>
                        ) : plan.id === 'custom' ? (
                          <a
                            className="dash-btn dash-btn--secondary dash-btn--full"
                            href="mailto:hello@chatflo.ai?subject=Custom Plan Inquiry"
                            style={{display:'block', textAlign:'center', textDecoration:'none'}}
                          >
                            Contact Sales
                          </a>
                        ) : (
                          <button
                            className="dash-btn dash-btn--primary dash-btn--full"
                            onClick={() => handleSubscribe(plan.id)}
                            disabled={billingLoading}
                          >
                            {billingLoading ? 'Processing...' : `Subscribe to ${plan.name}`}
                          </button>
                        )}
                      </div>
                    )) : (
                      <p>Loading plans...</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {currentView === 'settings' && (
            <div className="dash-settings">
              {!settingsForm ? (
                <div className="dash-empty"><p>Loading settings...</p></div>
              ) : (
                <>
                  <div className="dash-settings__section">
                    <h3>Business Profile</h3>
                    <div className="dash-settings__grid">
                      <label className="dash-settings__field">
                        <span>Business Name</span>
                        <input value={settingsForm.name} onChange={e => setSettingsForm(f => ({...f, name: e.target.value}))} />
                      </label>
                      <label className="dash-settings__field">
                        <span>Phone</span>
                        <input value={settingsForm.phone} onChange={e => setSettingsForm(f => ({...f, phone: e.target.value}))} />
                      </label>
                      <label className="dash-settings__field">
                        <span>Website</span>
                        <input value={settingsForm.website} onChange={e => setSettingsForm(f => ({...f, website: e.target.value}))} placeholder="https://yourbusiness.com" />
                      </label>
                      <label className="dash-settings__field">
                        <span>Service Area</span>
                        <input value={settingsForm.serviceArea} onChange={e => setSettingsForm(f => ({...f, serviceArea: e.target.value}))} placeholder="e.g. Greater Boston area" />
                      </label>
                      <label className="dash-settings__field dash-settings__field--full">
                        <span>Business Hours</span>
                        <input value={settingsForm.hours} onChange={e => setSettingsForm(f => ({...f, hours: e.target.value}))} placeholder="e.g. Mon-Fri 9AM-5PM" />
                      </label>
                    </div>
                  </div>

                  <div className="dash-settings__section">
                    <h3>Widget &amp; AI</h3>
                    <div className="dash-settings__grid">
                      <label className="dash-settings__field dash-settings__field--full">
                        <span>Greeting Message</span>
                        <textarea rows={3} value={settingsForm.greeting} onChange={e => setSettingsForm(f => ({...f, greeting: e.target.value}))} placeholder="Hello! How can I help you today?" />
                      </label>
                      <label className="dash-settings__field">
                        <span>AI Tone</span>
                        <select value={settingsForm.aiTone} onChange={e => setSettingsForm(f => ({...f, aiTone: e.target.value}))}>
                          <option value="professional">Professional</option>
                          <option value="friendly">Friendly</option>
                          <option value="formal">Formal</option>
                          <option value="casual">Casual</option>
                          <option value="enthusiastic">Enthusiastic</option>
                        </select>
                      </label>
                      <label className="dash-settings__field">
                        <span>Calendly Booking URL</span>
                        <input value={settingsForm.calendlyUrl} onChange={e => setSettingsForm(f => ({...f, calendlyUrl: e.target.value}))} placeholder="https://calendly.com/your-link" />
                      </label>
                    </div>
                  </div>

                  <div className="dash-settings__section">
                    <div className="dash-settings__section-head">
                      <h3>Services</h3>
                      <button className="dash-btn dash-btn--secondary" onClick={() => setSettingsForm(f => ({...f, services: [...f.services, {name:'',description:'',priceRange:'',duration:60}]}))}>
                        + Add Service
                      </button>
                    </div>
                    {settingsForm.services.length === 0 && (
                      <p style={{color:'var(--color-neutral-400)', fontSize:14}}>No services added yet. Click "Add Service" to get started.</p>
                    )}
                    {settingsForm.services.map((svc, idx) => (
                      <div key={idx} className="dash-settings__service">
                        <div className="dash-settings__service-fields">
                          <input placeholder="Service name *" value={svc.name} onChange={e => updateService(idx, 'name', e.target.value)} />
                          <input placeholder="Price range (e.g. $100–$200)" value={svc.priceRange} onChange={e => updateService(idx, 'priceRange', e.target.value)} />
                          <input placeholder="Duration (min)" type="number" value={svc.duration} onChange={e => updateService(idx, 'duration', e.target.value)} />
                        </div>
                        <div className="dash-settings__service-bottom">
                          <input className="dash-settings__service-desc" placeholder="Description (optional)" value={svc.description} onChange={e => updateService(idx, 'description', e.target.value)} />
                          <button className="dash-settings__remove" onClick={() => setSettingsForm(f => ({...f, services: f.services.filter((_,i)=>i!==idx)}))} title="Remove service">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="dash-settings__footer">
                    <button className="dash-btn dash-btn--primary" onClick={handleSaveSettings} disabled={settingsSaving}>
                      {settingsSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    {settingsSaved && <span className="dash-settings__saved">Changes saved</span>}
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

export default Dashboard;